import { useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { GlassIcon } from "./glass-icon";
import { GlassOrb } from "./glass-orb";

export type OrbitStep = {
  label: string;
  description?: string;
  icon: LucideIcon;
};

// Match Tailwind's `md` breakpoint. Below it the desktop orbit isn't visible,
// so it's never mounted — no SVG, no animation, no hydration cost on mobile.
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isDesktop;
}

export function OrbitLoop({
  steps,
  durationMs = 14000,
}: {
  steps: OrbitStep[];
  durationMs?: number;
}) {
  const isDesktop = useIsDesktop();
  const [active, setActive] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Advance the highlighted step on a timer rather than a per-frame rAF loop:
  // one re-render every few seconds instead of ~60/s. The traveling dot's
  // motion is pure CSS (see `.animate-orbit-dot`), so it needs no React state.
  // Runs only while the section is on screen and the user allows motion.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const stepMs = durationMs / steps.length;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer == null) {
        timer = setInterval(() => setActive((i) => (i + 1) % steps.length), stepMs);
      }
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const io = new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()), {
      threshold: 0.2,
    });
    io.observe(el);
    return () => {
      stop();
      io.disconnect();
    };
  }, [steps.length, durationMs]);

  return (
    <div ref={containerRef}>
      {isDesktop ? (
        <DesktopOrbit steps={steps} active={active} durationMs={durationMs} />
      ) : (
        <MobileSteps steps={steps} active={active} />
      )}
    </div>
  );
}

const RADIUS = 210;

function DesktopOrbit({
  steps,
  active,
  durationMs,
}: {
  steps: OrbitStep[];
  active: number;
  durationMs: number;
}) {
  return (
    <div>
      <div className="relative mx-auto" style={{ height: 540, width: 540 }}>
        {/* Outer aurora */}
        <div
          aria-hidden
          className="absolute inset-0 rounded-full blur-3xl opacity-40 animate-pulse-glow"
          style={{
            background: "radial-gradient(circle, rgba(217,70,239,0.45), transparent 65%)",
          }}
        />

        {/* Rotating dashed ring (decorative) */}
        <svg
          aria-hidden
          className="absolute inset-0 w-full h-full animate-spin-slow"
          viewBox="-270 -270 540 540"
        >
          <circle
            cx="0"
            cy="0"
            r={RADIUS - 28}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
            strokeDasharray="2 6"
          />
        </svg>

        {/* Orbit ring (static) */}
        <svg aria-hidden className="absolute inset-0 w-full h-full" viewBox="-270 -270 540 540">
          <defs>
            <linearGradient id="orbit-stroke" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="rgba(244,114,208,0.55)" />
              <stop offset="50%" stopColor="rgba(217,70,239,0.85)" />
              <stop offset="100%" stopColor="rgba(123,63,242,0.55)" />
            </linearGradient>
          </defs>
          <circle
            cx="0"
            cy="0"
            r={RADIUS}
            fill="none"
            stroke="url(#orbit-stroke)"
            strokeWidth="1.5"
          />
        </svg>

        {/* Traveling dot — rotates around the centre via CSS, no React state.
            The dot sits at the top (0, -RADIUS) and the whole <svg> spins. */}
        <svg
          aria-hidden
          className="absolute inset-0 w-full h-full animate-orbit-dot"
          style={{ animationDuration: `${durationMs}ms` }}
          viewBox="-270 -270 540 540"
        >
          <defs>
            <radialGradient id="dot-glow">
              <stop offset="0%" stopColor="rgba(255,255,255,1)" />
              <stop offset="40%" stopColor="rgba(244,114,208,0.95)" />
              <stop offset="100%" stopColor="rgba(244,114,208,0)" />
            </radialGradient>
          </defs>
          {/* Pulse halo */}
          <circle cx="0" cy={-RADIUS} r="22" fill="url(#dot-glow)" opacity="0.85" />
          {/* Dot */}
          <circle cx="0" cy={-RADIUS} r="5.5" fill="#fff" />
        </svg>

        {/* Central orb + caption */}
        <div className="absolute inset-0 grid place-items-center">
          <div className="relative">
            <GlassOrb size="md" />
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/85 font-semibold">
                Axy.digital
              </span>
            </div>
          </div>
        </div>

        {/* Step nodes */}
        {steps.map((step, i) => {
          const angle = -Math.PI / 2 + (i / steps.length) * 2 * Math.PI;
          const x = Math.cos(angle) * RADIUS;
          const y = Math.sin(angle) * RADIUS;
          // Nudge the bottom-left node (step 4 in a 5-step orbit) up a bit
          // so its chip doesn't visually overlap with the bottom-right one.
          const yNudge = steps.length === 5 && i === 3 ? -72 : 0;
          const isActive = active === i;
          return (
            <div
              key={step.label}
              className="absolute top-1/2 left-1/2 transition-all duration-500"
              style={{
                transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y + yNudge}px)) scale(${isActive ? 1.08 : 1})`,
                zIndex: isActive ? 5 : 2,
              }}
            >
              <div
                className={`glass-card px-4 py-3 flex items-center gap-2.5 transition-all duration-500 ${
                  isActive
                    ? "shadow-[0_18px_60px_rgba(217,70,239,0.55)] border-white/30"
                    : "opacity-80"
                }`}
                style={{ minWidth: 150 }}
              >
                <GlassIcon icon={step.icon} size="sm" />
                <span className="text-sm font-semibold text-white whitespace-nowrap">
                  {step.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Caption */}
      <div className="mt-8 mx-auto max-w-xl">
        <div className="glass-card px-6 py-4 text-center transition-all duration-300">
          <p className="text-[10px] uppercase tracking-[0.22em] text-pink-soft mb-1">
            Step {active + 1} of {steps.length}
          </p>
          <p key={active} className="text-sm text-white/85 animate-fade-in">
            <span className="font-semibold text-white">{steps[active].label}</span>
            {steps[active].description ? <> · {steps[active].description}</> : null}
          </p>
        </div>
      </div>
    </div>
  );
}

function MobileSteps({ steps, active }: { steps: OrbitStep[]; active: number }) {
  return (
    <div className="flex flex-col gap-3">
      {steps.map((step, i) => {
        const isActive = active === i;
        return (
          <div
            key={step.label}
            className={`glass-card p-4 flex items-start gap-3 transition-all duration-500 ${
              isActive ? "border-white/30 shadow-[0_10px_40px_rgba(217,70,239,0.45)]" : "opacity-75"
            }`}
          >
            <GlassIcon icon={step.icon} size="sm" />
            <div>
              <p className="text-sm font-semibold text-white">{step.label}</p>
              {step.description && <p className="text-xs text-white/60 mt-1">{step.description}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
